// src/components/pages/Dashboard.js
import React from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/material";
import { Icon } from "../icons";
import StatusBadge from "../components/ui/StatusBadge";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../constants/color";
import { clientsData, engineersData, projectsData } from "../data/dummyData";
import ChecklistLifecycleChart from "../components/charts/ChecklistLifecycleChart";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import axios from "axios";
import { url } from "../constants/url";
import ClientNotificationBanner from "../components/ClientNotificationBanner";
const Dashboard = () => {
  const stats = {
    totalProjects: projectsData.length,
    activeProjects: projectsData.filter((p) => p.status === "Active").length,
    completedProjects: projectsData.filter((p) => p.status === "Completed")
      .length,
    totalClients: clientsData.length,
    activeClients: clientsData.filter((c) => c.status === "Active").length,
    totalEngineers: engineersData.length,
    activeEngineers: engineersData.filter((e) => e.status === "Active").length,
  };

  const [lifecycleStats, setLifecycleStats] = useState(null);
  const [checklistStats, setChecklistStats] = useState([]);
  const [showAllChecklists, setShowAllChecklists] = useState(false);
  const [clientEngineerStats, setClientEngineerStats] = useState(null);
  const [individualClientStats, setIndividualClientStats] = useState(null);
  const navigate = useNavigate();
  const { isClient, isEngineer, userData } = useAuth();

  const isIndividualClient = isClient() && (userData?.clientRole?.roleType === "Individual" || userData?.clientRoleType === "Individual");

  const [topEngineers, setTopEngineers] = useState([]);
  const [showAllEngineers, setShowAllEngineers] = useState(false);

  const [visitedProjects, setVisitedProjects] = useState([]);
  const [showAllVisits, setShowAllVisits] = useState(false);

  useEffect(() => {
    const fetchVisitedProjects = async () => {
      try {
        const res = await axios.get(
          `${url}/api/reports/engineers/visited-projects`
        );
        setVisitedProjects(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch visited projects", err);
      }
    };

    fetchVisitedProjects();
  }, []);

  const visibleVisitedProjects = showAllVisits
    ? visitedProjects
    : visitedProjects.slice(0, 5);

  const formatVisitDate = (date) => {
    if (!date) return "-";

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const input = new Date(date);
    input.setHours(0, 0, 0, 0);

    if (input.getTime() === today.getTime()) return "Today";
    if (input.getTime() === tomorrow.getTime()) return "Tomorrow";

    return input.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    const fetchTopEngineers = async () => {
      const res = await axios.get(`${url}/api/reports/top-engineers`);
      setTopEngineers(res.data.data || []);
    };

    fetchTopEngineers();
  }, []);

  useEffect(() => {
    const fetchChecklistStats = async () => {
      const res = await axios.get(
        `${url}/api/dashboard/site-checklist-requests`
      );
      setChecklistStats(res.data.data);
    };

    fetchChecklistStats();
  }, []);

  const filteredChecklists = checklistStats.filter(
    (item) => item.noOfRequests > 0
  );

  const visibleChecklists = showAllChecklists
    ? filteredChecklists
    : filteredChecklists.slice(0, 8);

  const visibleEngineers = showAllEngineers
    ? topEngineers
    : topEngineers.slice(0, 7);

  const formatShortDate = (date) => {
    if (!date) return "--";
    return new Date(date).toLocaleDateString("en-GB", {
      month: "2-digit",
      year: "2-digit",
    });
  };

  useEffect(() => {
    const fetchLifecycleStats = async () => {
      try {
        const res = await axios.get(
          `${url}/api/dashboard/checklist-lifecycle-stats`
        );
        setLifecycleStats(res.data);
      } catch (err) {
        console.error("Lifecycle stats fetch failed", err);
      }
    };

    fetchLifecycleStats();
  }, []);

  useEffect(() => {
    const fetchClientEngineerStats = async () => {
      try {
        const res = await axios.get(
          `${url}/api/dashboard/client-engineer-stats`
        );
        setClientEngineerStats(res.data);
      } catch (err) {
        console.error("Client engineer stats fetch failed", err);
      }
    };

    fetchClientEngineerStats();
  }, []);

  useEffect(() => {
    const fetchIndividualClientStats = async () => {
      try {
        const res = await axios.get(`${url}/api/dashboard/individual-client-stats`);
        if (res.data.success) {
          setIndividualClientStats(res.data);
        }
      } catch (err) {
        console.error("Individual client stats fetch failed", err);
      }
    };

    if (isIndividualClient) {
      fetchIndividualClientStats();
    }
  }, [isIndividualClient]);

  return (
    <Box>
      <ClientNotificationBanner />
      {/* Stats Grid */}
      {/* Stats Grid */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          mb: 4,
          "& > div": {
            width: "250px",
            flexShrink: 0
          }
        }}
      >
        <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: '100%' }}>
            <Icon name="clients" stroke={2} size={46} color={COLORS.SECONDARY} />
            <Box paddingLeft={1}>
              <Typography
                sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H2, color: COLORS.SECONDARY }}
                paddingBottom={0.3}
              >
                {clientEngineerStats?.totalClients || 0}
              </Typography>
              <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                Total Clients
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: '100%' }}>
            <Icon name="engineers" stroke={2} size={46} color={COLORS.SECONDARY} />
            <Box paddingLeft={1}>
              <Typography
                sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H2, color: COLORS.SECONDARY }}
                paddingBottom={0.3}
              >
                {clientEngineerStats?.totalEngineers || 0}
              </Typography>
              <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                Total Engineers
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: '100%' }}>
            <Icon name="clock" stroke={2} size={46} color={COLORS.SECONDARY} />
            <Box paddingLeft={1}>
              <Typography
                sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H2, color: COLORS.SECONDARY }}
                paddingBottom={0.3}
              >
                {clientEngineerStats?.pendingRequests || 0}
              </Typography>
              <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                Pending Projects
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: '100%' }}>
            <Icon name="projects" stroke={2} size={46} color={COLORS.SECONDARY} />
            <Box paddingLeft={1}>
              <Typography
                sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H2, color: COLORS.SECONDARY }}
                paddingBottom={0.3}
              >
                {clientEngineerStats?.totalCompleted || 0}
              </Typography>
              <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                Completed Projects
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: '100%' }}>
            <Icon name="check-circle" stroke={2} size={46} color={COLORS.SECONDARY} />
            <Box paddingLeft={1}>
              <Typography
                sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H2, color: COLORS.SECONDARY }}
                paddingBottom={0.3}
              >
                {clientEngineerStats?.totalRequested || 0}
              </Typography>
              <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                Total Requests
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Admin Added Projects block removed as per request */}
      </Box>

      {/* Individual Client Sq Ft & Plan Breakdown */}
      {isIndividualClient && (
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mb: 2, fontWeight: 700 }}>
            Property Plan & Area (Sq Ft) Requests Summary
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)"
              },
              gap: 2
            }}
          >
            {["1 BHK", "2 BHK", "3 BHK", "4 BHK", "Bungalow", "Shop / Office (Up to 1000 sq ft)"].map((planType) => {
              const data = individualClientStats?.planBreakdown?.[planType] || {
                totalRequestsCount: 0,
                completedCount: 0,
                totalSqftSent: 0,
                completedSqft: 0
              };
              return (
                <Box
                  key={planType}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#fdfbff",
                    border: `1.5px solid ${COLORS.LIGHT_SECONDARY}60`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5
                  }}
                >
                  <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, fontWeight: 700 }}>
                    {planType}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                    <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT }}>
                      Total Sq Ft Sent:
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_SMALL, fontWeight: 700, color: COLORS.PRIMARY }}>
                      {data.totalSqftSent.toLocaleString()} sq ft
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT }}>
                      Completed Sq Ft:
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_SMALL, fontWeight: 700, color: COLORS.SUCCESS || "#2e7d32" }}>
                      {data.completedSqft.toLocaleString()} sq ft
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5, pt: 0.5, borderTop: "1px dashed #e0e0e0" }}>
                    <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_SMALL, color: "#666" }}>
                      Requests (Total / Completed):
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_SMALL, fontWeight: 600, color: COLORS.TEXT }}>
                      {data.totalRequestsCount} / {data.completedCount}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 4,
          mb: 4,
          alignItems: "stretch",
        }}
      >
        {/* Checklist Lifecycle Chart */}
        <Paper
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "360px",
            backgroundColor: COLORS.BACKGROUND,
          }}
        >
          <Box sx={{ display: "flex", flex: 1 }}>
            <ChecklistLifecycleChart stats={lifecycleStats} />
          </Box>
        </Paper>
        {/*list of checklist no/total */}
        <Paper
          sx={{
            p: 3,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            height: "360px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                ...TYPOGRAPHY.H3,
                color: COLORS.PRIMARY,
              }}
            >
              Site Checks Reports
            </Typography>


            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                ...TYPOGRAPHY.BODY_SMALL,
                color: COLORS.PRIMARY,
                cursor: "pointer",
              }}
              onClick={() => navigate('/settings/checklists')}
            >
              View All
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {visibleChecklists.map((item) => (
              <Box
                key={item._id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    ...TYPOGRAPHY.H4,
                    color: COLORS.PRIMARY,
                    maxWidth: "70%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.title}
                </Typography>

                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    ...TYPOGRAPHY.H4,
                    color: COLORS.PRIMARY,
                    fontWeight: 600,
                  }}
                >
                  {item.completedRequests} / {item.noOfRequests}
                </Typography>
              </Box>
            ))}

            {filteredChecklists.length === 0 && (
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.MUTED_TEXT,
                  textAlign: "center",
                  mt: 2,
                }}
              >
                No checklist reports available
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 4,
          mb: 4,
          alignItems: "stretch",
        }}
      >
        {/* engineer list top performer */}
        <Paper
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "360px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                ...TYPOGRAPHY.H3,
                color: COLORS.PRIMARY,
              }}
            >
              Top Engineers
            </Typography>

            {/* {!(isClient() || isEngineer()) && (
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.PRIMARY,
                  cursor: "pointer",
                }}
              >
                View All
              </Typography>
            )} */}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {visibleEngineers.map((engineer) => (
              <Box
                key={engineer._id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    ...TYPOGRAPHY.H4,
                    color: COLORS.PRIMARY
                  }}
                >
                  {engineer.name}
                </Typography>

                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    ...TYPOGRAPHY.H4,
                    color: COLORS.PRIMARY
                  }}
                >
                  {engineer.projectsRecords.projectCompleted} /{" "}
                  {engineer.projectsRecords.projectAssigned}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* visited date  */}
        <Paper
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "360px",
          }}
        >
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                ...TYPOGRAPHY.H3,
                color: COLORS.PRIMARY,
              }}
            >
              Site Visits
            </Typography>

            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                ...TYPOGRAPHY.BODY_SMALL,
                color: COLORS.PRIMARY,
                cursor: "pointer",
                fontWeight: 500,
              }}
              onClick={() => navigate('/calendar')}
            >
              See More
            </Typography>
          </Box>

          {/* List */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {visibleVisitedProjects.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ maxWidth: "75%" }}>
                  <Typography
                    sx={{
                      fontFamily: FONT_FAMILY,
                      ...TYPOGRAPHY.H4,
                      color: COLORS.PRIMARY,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.projectName}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: FONT_FAMILY,
                      ...TYPOGRAPHY.BODY_STANDARD,
                      color: COLORS.TEXT,
                    }}
                  >
                    {item.engineerName} • {item.clientName}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    ...TYPOGRAPHY.H4,
                    color: COLORS.PRIMARY,
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatVisitDate(item.visitDate)}
                </Typography>
              </Box>
            ))}

            {visitedProjects.length === 0 && (
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.MUTED_TEXT,
                  textAlign: "center",
                }}
              >
                No site visits found
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;
